import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';

import { GlobalReducerState } from 'app/reducers';
import { makeSelectJvpField } from 'Containers/App/selectors';
import { getEmployerSlugFromLocation } from 'Containers/App/utils';
import { PageLayout } from 'DesignLibrary/atoms/PageLayout';
import { JVPBootstrapPublicationInfo } from 'Utils/apiTypes';
import { WELCOME_PATH, getEnvironmentFromUrl } from 'Utils/urls';

import { Container, FullScreenContainer, LoadingContainer } from './styled';
import { DEPLOY_ENVIRONMENTS } from '../../constants';

interface ChatNgConfig {
  chat_ng?: any;
}

interface AskAlexPageStateProps {
  activePublicationInfo: JVPBootstrapPublicationInfo | null;
  upcomingPublicationInfo: JVPBootstrapPublicationInfo | null;
}

export type AskAlexPageProps = AskAlexPageStateProps;

// Function to get the appropriate Chat NG URL based on environment
const getChatNgUrl = (): string => {
  const deployedEnv = getEnvironmentFromUrl();

  if (deployedEnv === DEPLOY_ENVIRONMENTS.PROD) {
    return window._env_?.REACT_APP_CHAT_NG_PROD_URL || 'https://ask-alex-frontend.jellysecure.com';
  }

  // Default to staging for all other environments
  return window._env_?.REACT_APP_CHAT_NG_STAGING_URL || 'https://ask-alex-frontend.jellydevs.com';
};

// Custom hook to fetch and check for chat_ng in builder config from multiple sources
const useChatNgConfig = (dataRevisionLocations: string[]) => {
  const [config, setConfig] = useState<ChatNgConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validLocations = dataRevisionLocations.filter(Boolean);

    if (validLocations.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Fetch all configurations concurrently
    const fetchPromises = validLocations.map((location) =>
      fetch(location)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to fetch config from ${location}: ${response.status}`);
          }
          return response.json();
        })
        .then((data: ChatNgConfig) => ({ data, location })),
    );

    Promise.allSettled(fetchPromises)
      .then((results) => {
        // Check if any of the successful configs have chat_ng enabled
        const successfulConfigs = results
          .filter(
            (result): result is PromiseFulfilledResult<{ data: ChatNgConfig; location: string }> =>
              result.status === 'fulfilled',
          )
          .map((result) => result.value.data);

        // Find the first config with chat_ng enabled, or use the first successful config
        const enabledConfig =
          successfulConfigs.find((config) => config.chat_ng !== undefined) || successfulConfigs[0] || null;

        setConfig(enabledConfig);

        // Set error only if all requests failed
        if (successfulConfigs.length === 0) {
          const failedResults = results.filter(
            (result) => result.status === 'rejected',
          ) as PromiseRejectedResult[];
          setError(failedResults.map((result) => result.reason.message).join('; '));
        }

        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [dataRevisionLocations]);

  return { config, loading, error };
};

export const AskAlexPage = ({ activePublicationInfo, upcomingPublicationInfo }: AskAlexPageProps) => {
  const navigate = useNavigate();

  // Get the customer slug from the current URL
  const customerSlug = getEmployerSlugFromLocation(window.location);

  // Get data revision locations from both publications
  const activeDataRevisionLocation = activePublicationInfo?.publication?.data_revision_location;
  const upcomingDataRevisionLocation = upcomingPublicationInfo?.publication?.data_revision_location;
  const dataRevisionLocations = useMemo(
    () => [activeDataRevisionLocation, upcomingDataRevisionLocation].filter(Boolean) as string[],
    [activeDataRevisionLocation, upcomingDataRevisionLocation],
  );

  // Get target value from publications (prioritize active, fall back to upcoming)
  const target = activePublicationInfo?.publication?.target || upcomingPublicationInfo?.publication?.target;

  // Fetch builder configs to check for chat_ng in either publication
  const { config, loading, error } = useChatNgConfig(dataRevisionLocations);

  // Check if chat_ng is enabled
  const isChatNgEnabled = config?.chat_ng !== undefined;

  // Redirect to landing page when chat is not available
  useEffect(() => {
    // Only redirect if we've finished loading and there's an error or chat is disabled
    if (!loading && (error || (config !== null && !isChatNgEnabled))) {
      navigate(WELCOME_PATH);
    }
  }, [loading, error, isChatNgEnabled, config, navigate]);

  if (loading || error || dataRevisionLocations.length === 0) {
    return (
      <PageLayout page="ask-alex">
        <Container>
          <LoadingContainer>Loading chat configuration...</LoadingContainer>
        </Container>
      </PageLayout>
    );
  }

  if (!isChatNgEnabled) {
    return null; // Redirect handled by useEffect
  }

  // Construct iframe URL with target parameter
  const iframeUrl = `${getChatNgUrl()}/standalone/${customerSlug}${target ? `?target=${target}` : ''}`;

  return (
    <FullScreenContainer>
      <iframe src={iframeUrl} title="Ask Alex Chat" />
    </FullScreenContainer>
  );
};

const mapStateToProps = createStructuredSelector<GlobalReducerState, AskAlexPageStateProps>({
  activePublicationInfo: makeSelectJvpField('active'),
  upcomingPublicationInfo: makeSelectJvpField('upcoming'),
});

const withConnect = connect(mapStateToProps);

export default withConnect(AskAlexPage);
